/**
 * 69.3b, the ship as a body with parts — step 1 (BUILDLIST 69.3b's note; the owner, Oct 20: "maybe time to make dropship
 * interactible / shootable / have parts now"; DECISIONS 211 (2), 207 (4)): the dropship's **hit shape** and its **parts
 * table**, as data and a pure pose — nothing in the sim reads them yet (step 2 wires them into sight, the rounds and the
 * blasts behind `shipsDestructible`; off, every byte as before).
 *
 * The shape is the look's (rendering/dropshipLooks.ts DROPSHIP, in people: its measures ÷ 1.8), in the ship's frame — +z
 * forward, x across (+x the ship's left), y up, the origin on the ground under the ship standing on its legs (the anchors'
 * frame, constants/dropship.ts): the **hull** (the body, the tail and the two sponsons: four boxes, unmarked — the body's
 * own health), the **cockpit** (the cab's box at the nose), the **four nacelles** (a box each on its shaft at the sponsons'
 * corners, **turned about the ship's x axis by the tilt** — upright at a standstill and the hover, aft at cruise:
 * `nacelleTiltFor`'s rule from the flight's speed and acceleration), and the **slung load** while it is carried in the air
 * — the troop container's shut box in the slot (`HITBOXES.dropContainer`), or a vehicle's hull lowered on the cables by
 * the pose's `lower`; landed, the container is a structure of its own (the obstacle fix: core/dropship.ts
 * syncShipContainers) and leaves the ship's shape. Posed per tick from `dropshipPose` (a pure function of the tick), each
 * ship a `HitShape` like a vehicle's hull in core/hits.ts movingShapes (kept apart from the planet's filed shapes), its
 * bound **swept**: the bounding cylinder widened by the tick's move, so a fast body's miss test stays honest against a
 * round's substep (40 people a second is a third of a person a tick). The parked wave ship on its pad (DECISIONS 207 (1))
 * is posed standing on the pad's deck, its nacelles upright.
 *
 * `SHIP_PARTS` is the marked parts' table as `STRUCTURE_PARTS` is (constants/damage.ts): the nacelles `function`, a
 * share of 0.15 each, detaching; the cockpit `function`, 0.2; the load its own body; the hull the rest. Step 2 reads it.
 */
import type { GameState, DropshipFlight, Vec3 } from '../types';
import type { MarkedPart } from '../constants/damage';
import type { HitPart } from '../types/body';
import { HITBOXES } from '../constants/hitboxes';
import { DROPSHIP } from '../constants/action';
import { DROPSHIP_ANCHORS } from '../constants/dropship';
import { HELI, HELI_PAD } from '../constants/helicopter';
import { PHYS_DT } from '../constants/physics';
import { parkedShips } from './dropship';
import { parkedHelis, parkedId, type ParkedHeli } from './helipad';   // 69.74 step 2c
import { flightNow, lighterNow } from './shipNow';
import { crashShapes } from './shipCrash';   // (a cycle by design: the crash poses its shape through shipShape)
import { LIGHTER_BODY, LIGHTER_ID_BASE } from './lighterBody';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { personSurfaceRadius } from './fineTerrain';
import { rightOf } from './surfaceWalk';
import { dcos, dsin } from './dmath';
import { vecAdd, vecScale, vecNormalize, vecSub, vecDot } from '../math';
import type { HitShape, Prim } from './hits';

/** A ship's shape: a hit shape whose reference is a ship's (a flight's id; a parked ship −1 − its team), kept apart from the obstacles' kinds (a ship is never an obstacle on the ground). */
export interface ShipShape extends Omit<HitShape, 'ref'> { ref: { kind: 'ship'; id: number } }

/** The look's measures in people (rendering/dropshipLooks.ts DROPSHIP: a person is 1.8 there). */
const U = 1 / 1.8;
/**
 * The airframe's rise over its drawn line (the look's DROPSHIP.lift, 0.9 since design-2's frame fit raised the ship 0.75
 * so a vehicle in its shipping frame hangs under it): the hull's parts are drawn with the strongback at 3.35 and ride
 * `lift` higher; read off the anchors' belly (the strongback's height over the ground), so a raise of the ship moves the
 * boxes with it. (Step 1's boxes sat on the drawn line, 0.08 people low at the old lift of 0.15.)
 */
const LIFT = DROPSHIP_ANCHORS.belly - 3.35 * U;
/** The nacelles' shafts: x across, y up, z the front and the rear pair (people). */
const NACELLE = { x: 3.05 * U, y: 4.05 * U + LIFT, z: [1.75 * U, -2.55 * U] as const };
const NACELLE_HALF: [number, number, number] = [0.23, 0.33, 0.6];   // (the housing inside the footprint's outline: x to 1.92)

/**
 * The ship's own parts (the ship's frame, people, each y the drawn line's plus LIFT; `tilt`: a nacelle box turned about x — 0 along z (cruise),
 * 1 along y (upright)). In order: the body, the tail, the two sponsons (the hull), the cockpit, the four nacelles (front left, front
 * right, rear left, rear right).
 */
export const SHIP_BODY: readonly HitPart[] = [
  { shape: 'box', centre: [0, 2.21 + LIFT, 0.06], half: [1.0, 0.35, 2.22] },        // 0 the body (BODY: z −3.9 … 4.1, y 3.35 … 4.62 drawn, half-width 1.8)
  { shape: 'box', centre: [0, 2.26 + LIFT, -2.29], half: [1.0, 0.26, 0.46] },       // 1 the tail (TAIL)
  { shape: 'box', centre: [-1.19, 2.2 + LIFT, -0.22], half: [0.19, 0.3, 1.8] },     // 2 the left sponson (SPONSON at x −2.15)
  { shape: 'box', centre: [1.19, 2.2 + LIFT, -0.22], half: [0.19, 0.3, 1.8] },      // 3 the right sponson
  { shape: 'box', centre: [0, 2.44 + LIFT, 1.8], half: [0.72, 0.47, 0.86] },        // 4 the cockpit (CAB; the crew doors at x ±1.3)
  { shape: 'box', centre: [NACELLE.x, NACELLE.y, NACELLE.z[0]], half: [...NACELLE_HALF] },    // 5 front left nacelle
  { shape: 'box', centre: [-NACELLE.x, NACELLE.y, NACELLE.z[0]], half: [...NACELLE_HALF] },   // 6 front right
  { shape: 'box', centre: [NACELLE.x, NACELLE.y, NACELLE.z[1]], half: [...NACELLE_HALF] },    // 7 rear left
  { shape: 'box', centre: [-NACELLE.x, NACELLE.y, NACELLE.z[1]], half: [...NACELLE_HALF] },   // 8 rear right
];
/** The nacelles' indices in SHIP_BODY. */
export const SHIP_NACELLES: readonly number[] = [5, 6, 7, 8];
/** The cockpit's index. */
export const SHIP_COCKPIT = 4;

/** The marked parts (the table STRUCTURE_PARTS keeps for structures): the nacelles, the cockpit; the hull is the body's own health, the load its own body. */
export const SHIP_PARTS: readonly MarkedPart[] = [
  { part: 5, role: 'function', share: 0.15, detaches: true }, { part: 6, role: 'function', share: 0.15, detaches: true },
  { part: 7, role: 'function', share: 0.15, detaches: true }, { part: 8, role: 'function', share: 0.15, detaches: true },
  { part: SHIP_COCKPIT, role: 'function', share: 0.2 },
];
/** The ship's health (its hull's one number, step 2) and the cruise the tilt reads against (people a second). */
export const SHIP_BODY_HEALTH = 400;

/**
 * 69.59 step 1: the helicopter's body (lane H's look C, rendering/helicopterLooks.ts `armed`, its measures ÷ 1.8; its frame on its
 * wheels: the origin on the ground under it, +z forward, +x its left, y up): the cabin, the boom (the hull: unmarked), the
 * cockpit, the two engines on the roof, the tail rotor beside the fin. The main rotor's disc is no part (a box the disc's size
 * would take every round; its blades are mostly air) — the owner's lean names the tail rotor.
 */
export const HELI_BODY: readonly HitPart[] = [
  { shape: 'box', centre: [0, 1.01, 0.11], half: [0.67, 0.68, 1.33] },      // 0 the cabin (z −2.2 … 2.6 drawn, y 0.6 … 3.05, 2.4 wide)
  { shape: 'box', centre: [0, 1.35, -3.58], half: [0.28, 0.32, 1.75] },     // 1 the boom (z −3.3 … −9.6)
  { shape: 'box', centre: [0, 1.26, 2.0], half: [0.67, 0.43, 0.56] },       // 2 the cockpit (the nose, z 2.6 … 4.6)
  { shape: 'box', centre: [0.28, 1.88, -0.31], half: [0.21, 0.18, 1.31] },  // 3 the left engine (on the roof, z 1.8 … −2.9)
  { shape: 'box', centre: [-0.28, 1.88, -0.31], half: [0.21, 0.18, 1.31] }, // 4 the right engine
  { shape: 'box', centre: [-0.18, 2.22, -5.58], half: [0.06, 0.83, 0.83] }, // 5 the tail rotor (r 1.5 beside the fin at z −10.05)
];
/** The helicopter's cockpit, its engines and its tail rotor in HELI_BODY. */
export const HELI_COCKPIT = 2, HELI_ENGINES: readonly number[] = [3, 4], HELI_TAIL_ROTOR = 5;
/** 69.59 step 1: the helicopter's marked parts — the cockpit, the engines (one lost: it flies on), the tail rotor (lost: it spins in) — each a share of HELI.health. */
export const HELI_PARTS: readonly MarkedPart[] = [
  { part: HELI_COCKPIT, role: 'function', share: 0.2 },
  { part: 3, role: 'function', share: 0.15 }, { part: 4, role: 'function', share: 0.15 },
  { part: HELI_TAIL_ROTOR, role: 'function', share: 0.1, detaches: true },
];

/**
 * 69.59 step 1: a flight's body as the damage reads it — its hit parts, its marked parts' table, its lifters (two lost: down),
 * its cockpit, its tail rotor (a helicopter's: lost, down), its full health — a helicopter's (HELI_BODY) or the dropship's.
 */
export function shipBodyOf(f: Partial<Pick<DropshipFlight, 'craft'>> | undefined): { body: readonly HitPart[]; table: readonly MarkedPart[]; lifters: readonly number[]; cockpit: number; tail?: number; health: number } {
  return f?.craft === 'heli' ? { body: HELI_BODY, table: HELI_PARTS, lifters: HELI_ENGINES, cockpit: HELI_COCKPIT, tail: HELI_TAIL_ROTOR, health: HELI.health }
    : { body: SHIP_BODY, table: SHIP_PARTS, lifters: SHIP_NACELLES, cockpit: SHIP_COCKPIT, health: SHIP_BODY_HEALTH };
}

/** A ship's frame in the world: its origin (the ground point under it, lifted by its height), up, forward and left (the ship's +x). */
export interface ShipFrame { origin: Vec3; up: Vec3; front: Vec3; left: Vec3; tilt: number }

// 69.17 step 1: shipTilt and flightFrame live with the accessor (core/shipNow.ts), kept here for their readers
export { shipTilt, flightFrame } from './shipNow';

/** The parked wave ship's frame on its pad (standing on the deck, upright). */
export function parkedFrame(state: Pick<GameState, 'planets' | 'config' | 'shipPads'>, team: number): ShipFrame | null {
  const pad = state.shipPads?.[team], planet = pad ? state.planets[pad.planetIndex] : undefined;
  if (!pad || !planet) return null;
  const ps = personSetOf(state), up = vecNormalize(pad.dir);
  const r = personSurfaceRadius(planet, up, ps) + (pad.deck ?? 0);
  const front = vecNormalize(vecSub(pad.facing, vecScale(up, vecDot(pad.facing, up))));
  return { origin: vecAdd(planet.position, vecScale(up, r)), up, front, left: vecScale(rightOf(front, up), -1), tilt: 1 };
}

/** 69.74 step 2c: a helicopter parked on its spot: on the pad's deck there, facing the pad's front. */
export function parkedHeliFrame(state: Pick<GameState, 'planets' | 'config'>, h: Pick<ParkedHeli, 'planetIndex' | 'dir' | 'facing'>): ShipFrame {
  const planet = state.planets[h.planetIndex], ps = personSetOf(state), up = vecNormalize(h.dir);
  const r = personSurfaceRadius(planet, up, ps) + HELI_PAD.deckPeople * PERSON_BODY_HEIGHT * ps.k;
  const front = vecNormalize(vecSub(h.facing, vecScale(up, vecDot(h.facing, up))));
  return { origin: vecAdd(planet.position, vecScale(up, r)), up, front, left: vecScale(rightOf(front, up), -1), tilt: 1 };
}

/** A box part in the ship's frame as a world prim; `turn`: the part's own frame turned about the ship's x by this many radians (the nacelles' tilt). */
function boxPrim(part: HitPart, fr: ShipFrame, person: number, turn = 0): Prim {
  if (part.shape !== 'box') throw new Error('a ship part is a box');
  const [x, y, z] = part.centre, [hx, hy, hz] = part.half;
  const c = vecAdd(fr.origin, vecAdd(vecScale(fr.left, x * person), vecAdd(vecScale(fr.up, y * person), vecScale(fr.front, z * person))));
  if (turn === 0) return { cyl: false, c, ax: fr.left, ay: fr.up, az: fr.front, hx: hx * person, hy: hy * person, hz: hz * person };
  // about x (the ship's left): y → (y cos − z sin), z → (y sin + z cos)
  const cs = dcos(turn), sn = dsin(turn);
  const ay = vecAdd(vecScale(fr.up, cs), vecScale(fr.front, -sn)), az = vecAdd(vecScale(fr.up, sn), vecScale(fr.front, cs));
  return { cyl: false, c, ax: fr.left, ay, az, hx: hx * person, hy: hy * person, hz: hz * person };
}

/**
 * A ship's shape in the world: its parts' prims (SHIP_BODY in order, the nacelles turned by the tilt: upright at 1, aft at
 * 0), then the slung load's when `load` is given (the container's shut box in the slot, or a vehicle's hull lowered by
 * `lower` people on the cables) — and the bound: an upright cylinder round all of it, widened by `sweep` (world: the tick's
 * move) so a fast body's miss test stays honest. The `ref` is the flight's id (kind 'ship'; a parked ship −1 − team).
 */
export function shipShape(id: number, fr: ShipFrame, person: number, load: { parts: readonly HitPart[]; lower?: number } | null, sweep: number, body: readonly HitPart[] = SHIP_BODY): ShipShape {
  const prims: Prim[] = [];
  body.forEach((p, i) => prims.push(boxPrim(p, fr, person, body === SHIP_BODY && SHIP_NACELLES.includes(i) ? (fr.tilt * Math.PI) / 2 : 0)));   // (69.10 step 4: a lighter's own parts, no nacelles)   // (a nacelle's long axis: along z aft at cruise, along y upright at the hover)
  if (load) {
    const drop = (load.lower ?? 0), slotZ = DROPSHIP_ANCHORS.slotZ;
    for (const p of load.parts) {
      if (p.shape !== 'box') continue;
      prims.push(boxPrim({ ...p, centre: [p.centre[0], p.centre[1] - drop, p.centre[2] + slotZ] }, fr, person));
    }
  }
  let reach = 0, top = 0, bottom = Infinity;
  for (const p of prims) {
    if (p.cyl) continue;
    const rel = vecSub(p.c, fr.origin), h = vecDot(rel, fr.up), flat = vecSub(rel, vecScale(fr.up, h));
    const r = Math.sqrt(vecDot(flat, flat)) + Math.sqrt(p.hx * p.hx + p.hy * p.hy + p.hz * p.hz);
    reach = Math.max(reach, r); top = Math.max(top, h + Math.sqrt(p.hx * p.hx + p.hy * p.hy + p.hz * p.hz)); bottom = Math.min(bottom, h - Math.sqrt(p.hx * p.hx + p.hy * p.hy + p.hz * p.hz));
  }
  return { ref: { kind: 'ship', id }, dir: fr.up, prims, reach: reach + sweep, bound: { cyl: true, base: vecAdd(fr.origin, vecScale(fr.up, bottom - sweep)), up: fr.up, r0: reach + sweep, r1: reach + sweep, h0: 0, h1: top - bottom + 2 * sweep } };
}

const memo = new WeakMap<object, { tick: number; planetIndex: number; list: readonly ShipShape[]; lighters?: unknown }>();
const NONE: readonly ShipShape[] = [];

/**
 * The ships on planet `planetIndex` at `tick` as hit shapes: every flight in the air or on the ground there (posed by
 * `dropshipPose`), each with its load while it carries one in the air, its bound swept by its tick's move; and each
 * parked wave ship on its pad. Memoised on the flights list and the tick (a pure function of the state and the tick).
 * Nothing reads it yet (step 2 does, behind the switch).
 */
export function shipShapes(state: Pick<GameState, 'planets' | 'config' | 'dropships' | 'shipPads' | 'structures'> & Partial<Pick<GameState, 'lighters'>>, planetIndex: number, tick: number): readonly ShipShape[] {
  const flights = state.dropships?.flights ?? [];
  const pads = state.shipPads, runs = state.lighters?.runs ?? [];
  if (!flights.length && !pads && !state.dropships?.crashes?.length && !runs.length && !state.dropships?.pads) return NONE;
  const key: object = state.dropships ?? state.shipPads ?? state.lighters!;
  const hit = memo.get(key);
  if (hit && hit.tick === tick && hit.planetIndex === planetIndex && hit.lighters === state.lighters) return hit.list;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const list: ShipShape[] = [];
  for (const f of flights) {
    if (f.planetIndex !== planetIndex) continue;
    const now = flightNow(state, f, tick);   // 69.17 step 1: the ship as it is now
    if (!now) continue;
    const pose = now.flight!.pose, fr = now.frame, k = now.flight!.kin, sweep = k ? Math.abs(k.speed) * PHYS_DT : 0;
    let load: Parameters<typeof shipShape>[3] = null;
    if (pose.carrying && pose.phase !== 'down') {
      if (f.payload === 'vehicle' && f.vehicle) load = { parts: HITBOXES[f.vehicle].parts, lower: pose.lower };
      else load = { parts: HITBOXES.dropContainer.parts };
    } else if (pose.carrying && f.payload === 'vehicle' && f.vehicle) load = { parts: HITBOXES[f.vehicle].parts, lower: pose.lower };   // (a vehicle hangs under the hover till its wheels touch)
    list.push(f.craft === 'heli' ? shipShape(f.id, fr, person, null, sweep, HELI_BODY) : shipShape(f.id, fr, person, load, sweep));   // (69.59: a helicopter's own body, its riders in its cabin)
  }
  for (const p of parkedShips(state)) {
    if (p.planetIndex !== planetIndex) continue;
    const fr = parkedFrame(state, p.team);
    if (fr) list.push(shipShape(-1 - p.team, fr, person, null, 0));   // (its container stands as a structure of its own: the obstacle fix)
  }
  // 69.74 step 2c: the helicopters parked on their pads, each its own body (its id parkedId: the spot's)
  for (const h of state.dropships?.pads ? parkedHelis(state) : []) if (h.planetIndex === planetIndex) list.push(shipShape(parkedId(h.team, h.spot), parkedHeliFrame(state, h), person, null, 0, HELI_BODY));
  if (state.dropships?.crashes?.length) list.push(...crashShapes(state, planetIndex, tick));   // 69.3b, the ship's crash: the falling ships, on their arcs
  // 69.10 step 4: the lighters' runs, from the entry down and up again (targetable all the way: the owner)
  for (const r of runs) {
    if (r.planetIndex !== planetIndex) continue;
    const now = lighterNow(state, r, tick);
    if (!now) continue;
    list.push(shipShape(LIGHTER_ID_BASE + r.id, now.frame, person, null, now.lighter!.pose.speed * person * PHYS_DT, LIGHTER_BODY));
  }
  memo.set(key, { tick, planetIndex, list, lighters: state.lighters });
  return list;
}
